import * as THREE from 'three';
import { FLIP_U } from './constants';

interface CrossPoint {
  x: number;
  z: number;
  u: number;
  section: 'back' | 'front' | 'spine';
  px: number;
  nx: number;
  nz: number;
}

export interface CaseOpts {
  grooveDepth: number;
  grooveW: number;
  bowAmt: number;
  warpAmt: number;
  rows?: number;
}

// ─── Cross-section of the bent case (with French groove) ───────────
export function crossSection(
  Wu: number,
  Su: number,
  bulge: number,
  foreExt: number,
  grooveDepth: number,
  grooveW: number,
  Nspine = 30,
): CrossPoint[] {
  const foreX = Wu / 2 + foreExt;
  const hingeX = -Wu / 2;
  const total = 2 * Wu + Su;
  const bU = Wu / total,
    sU = (Wu + Su) / total;
  const span = foreX - hingeX;
  const pts: CrossPoint[] = [];
  const gStart = hingeX + grooveW; // panel-side start of the groove channel

  const grv = (dh: number) =>
    dh > 0 && dh < grooveW ? grooveDepth * Math.sin((Math.PI * dh) / grooveW) : 0;

  function pushBack(x: number) {
    const dh = x - hingeX;
    pts.push({
      x,
      z: -Su / 2 + grv(dh),
      u: (bU * (foreX - x)) / span,
      section: 'back',
      px: (x - hingeX) / span,
      nx: 0,
      nz: 0,
    });
  }
  function pushFront(x: number) {
    const dh = x - hingeX;
    pts.push({
      x,
      z: Su / 2 - grv(dh),
      u: sU + ((1 - sU) * (x - hingeX)) / span,
      section: 'front',
      px: (x - hingeX) / span,
      nx: 0,
      nz: 0,
    });
  }

  // back panel (coarse) foreX -> gStart
  const Nmain = 12;
  for (let i = 0; i < Nmain; i++) {
    const t = i / (Nmain - 1);
    pushBack(foreX + (gStart - foreX) * t);
  }
  // back groove (fine) gStart -> hingeX
  const Ng = 10;
  for (let i = 1; i <= Ng; i++) {
    const t = i / Ng;
    pushBack(gStart + (hingeX - gStart) * t);
  }
  // spine arc
  for (let i = 1; i <= Nspine; i++) {
    const t = i / Nspine;
    pts.push({
      x: hingeX - bulge * Math.sin(Math.PI * t),
      z: (-Su / 2) * Math.cos(Math.PI * t),
      u: bU + (sU - bU) * t,
      section: 'spine',
      px: 0,
      nx: 0,
      nz: 0,
    });
  }
  // front groove (fine) hingeX -> gStart
  for (let i = 1; i <= Ng; i++) {
    const t = i / Ng;
    pushFront(hingeX + (gStart - hingeX) * t);
  }
  // front panel (coarse) gStart -> foreX
  for (let i = 1; i < Nmain; i++) {
    const t = i / (Nmain - 1);
    pushFront(gStart + (foreX - gStart) * t);
  }

  // base outward normals (pre-displacement; used to offset the inner shell)
  for (let i = 0; i < pts.length; i++) {
    const a = pts[Math.max(0, i - 1)],
      b = pts[Math.min(pts.length - 1, i + 1)];
    let tx = b.x - a.x,
      tz = b.z - a.z;
    const L = Math.hypot(tx, tz) || 1;
    tx /= L;
    tz /= L;
    const p1 = [tz, -tx],
      p2 = [-tz, tx],
      P = pts[i];
    const d1 = p1[0] * P.x + p1[1] * P.z,
      d2 = p2[0] * P.x + p2[1] * P.z;
    const n = d1 > d2 ? p1 : p2;
    pts[i].nx = n[0];
    pts[i].nz = n[1];
  }
  return pts;
}

// ─── Build the case: subdivided grid with board-bow + warp ─────────
export function buildCase(
  Wu: number,
  Hu: number,
  Su: number,
  bulge: number,
  board: number,
  opts: CaseOpts,
): { artGeo: THREE.BufferGeometry; edgeGeo: THREE.BufferGeometry } {
  const grooveDepth = opts.grooveDepth,
    grooveW = opts.grooveW;
  const bowAmt = opts.bowAmt,
    warpAmt = opts.warpAmt,
    rows = opts.rows || 14;
  const pts = crossSection(Wu, Su, bulge, board * 1.6, grooveDepth, grooveW);
  const M = pts.length,
    R = rows;
  const yB = -Hu / 2,
    yT = Hu / 2;
  const uval = (u: number) => (FLIP_U ? 1 - u : u);

  // displacement along outward normal at cross-point p, height fraction v(0..1)
  function disp(p: CrossPoint, v: number): number {
    let d = 0;
    if (p.section !== 'spine') d += bowAmt * Math.sin(Math.PI * p.px) * Math.sin(Math.PI * v);
    if (warpAmt > 0) {
      const wy = v * Hu;
      d +=
        warpAmt *
        (Math.sin(p.x * 3.1 + wy * 2.3) * 0.5 + Math.sin(p.x * 1.7 - wy * 1.9 + 1.3) * 0.5);
    }
    return d;
  }

  // OUTER grid (M cols x R+1 rows), indexed
  const outPos: number[] = [],
    outUv: number[] = [];
  for (let j = 0; j <= R; j++) {
    const v = j / R,
      y = yB + (yT - yB) * v;
    for (let i = 0; i < M; i++) {
      const p = pts[i],
        d = disp(p, v);
      outPos.push(p.x + p.nx * d, y, p.z + p.nz * d);
      outUv.push(uval(p.u), v);
    }
  }
  const outIdx: number[] = [];
  for (let j = 0; j < R; j++) {
    for (let i = 0; i < M - 1; i++) {
      const a = j * M + i,
        b = a + 1,
        c = a + M,
        dd = c + 1;
      outIdx.push(a, b, dd, a, dd, c);
    }
  }
  const artGeo = new THREE.BufferGeometry();
  artGeo.setAttribute('position', new THREE.Float32BufferAttribute(outPos, 3));
  artGeo.setAttribute('uv', new THREE.Float32BufferAttribute(outUv, 2));
  artGeo.setIndex(outIdx);
  artGeo.computeVertexNormals();

  // INNER grid = outer offset inward by board (along computed outer normals)
  const nrm = artGeo.attributes.normal.array;
  const innPos: number[] = [];
  for (let k = 0; k < outPos.length; k += 3) {
    innPos.push(
      outPos[k] - nrm[k] * board,
      outPos[k + 1] - nrm[k + 1] * board,
      outPos[k + 2] - nrm[k + 2] * board,
    );
  }
  const edgePos: number[] = [],
    edgeIdx: number[] = [];
  const baseInner = edgePos.length / 3;
  for (let k = 0; k < innPos.length; k++) edgePos.push(innPos[k]);
  for (let j = 0; j < R; j++) {
    // inner surface, reversed winding
    for (let i = 0; i < M - 1; i++) {
      const a = baseInner + j * M + i,
        b = a + 1,
        c = a + M,
        dd = c + 1;
      edgeIdx.push(a, dd, b, a, c, dd);
    }
  }
  // helper to add a quad (two tris) by pushing fresh vertices
  function quad(P0: number[], P1: number[], P2: number[], P3: number[]) {
    const s = edgePos.length / 3;
    edgePos.push(...P0, ...P1, ...P2, ...P3);
    edgeIdx.push(s, s + 1, s + 2, s, s + 2, s + 3);
  }
  const O = (i: number, j: number) => [
    outPos[(j * M + i) * 3],
    outPos[(j * M + i) * 3 + 1],
    outPos[(j * M + i) * 3 + 2],
  ];
  const I = (i: number, j: number) => [
    innPos[(j * M + i) * 3],
    innPos[(j * M + i) * 3 + 1],
    innPos[(j * M + i) * 3 + 2],
  ];
  // top & bottom caps
  for (let i = 0; i < M - 1; i++) {
    quad(O(i, R), O(i + 1, R), I(i + 1, R), I(i, R)); // top
    quad(I(i, 0), I(i + 1, 0), O(i + 1, 0), O(i, 0)); // bottom
  }
  // fore-edge caps (both open ends)
  for (let j = 0; j < R; j++) {
    quad(O(0, j), I(0, j), I(0, j + 1), O(0, j + 1)); // back fore-edge
    quad(I(M - 1, j), O(M - 1, j), O(M - 1, j + 1), I(M - 1, j + 1)); // front fore-edge
  }
  const edgeGeo = new THREE.BufferGeometry();
  edgeGeo.setAttribute('position', new THREE.Float32BufferAttribute(edgePos, 3));
  edgeGeo.setIndex(edgeIdx);
  edgeGeo.computeVertexNormals();

  return { artGeo, edgeGeo };
}
